import { useTranslation } from 'react-i18next';
import { Paperclip, RefreshCw, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { IPendingBugReportAttachment } from '../hooks/use-bug-report-attachment-upload';
import { formatSize } from '../utils';

interface IPendingAttachmentListProps {
    readonly items: readonly IPendingBugReportAttachment[];
    readonly onRemove: (localId: string) => void;
}

/**
 * Files staged for a report or a reply.
 *
 * One compact row per file — clip, name, and the status as a muted suffix on
 * the SAME line. A file is one fact, and stacking its name over its size gave
 * every chip two lines of height plus a centred clip floating beside them,
 * which read as a card rather than a chip and pushed the composer down.
 * While uploading, a hairline progress bar sits along the bottom edge of the
 * row, so watching it land costs no extra height at all.
 *
 * Shared by the new-report form and the thread composer rather than copied:
 * proof is the whole point of a bug report, so the reporter needs to watch it
 * land in both places, and two copies of that would drift.
 */
export function PendingAttachmentList({ items, onRemove }: IPendingAttachmentListProps) {
    const { t } = useTranslation();

    if (items.length === 0) return null;

    return (
        <ul className="flex flex-col gap-1">
            {items.map((item) => {
                const status =
                    item.status === 'uploading'
                        ? t('bugReports.form.attachmentUploading', 'Uploading {{progress}}%', {
                              progress: item.progress,
                          })
                        : item.status === 'error'
                          ? t('bugReports.form.attachmentError', 'Upload failed')
                          : formatSize(item.sizeBytes);

                return (
                    <li
                        key={item.localId}
                        className={cn(
                            'relative flex items-center gap-2 overflow-hidden rounded-md border border-border-default py-1 pr-1 pl-2',
                            item.status === 'error' && 'border-error-300',
                        )}
                    >
                        <Paperclip className="size-3.5 shrink-0 text-text-tertiary" />
                        <span
                            className="min-w-0 flex-1 truncate text-xs text-text-primary"
                            title={item.fileName}
                        >
                            {item.fileName}
                        </span>
                        <span
                            className={cn(
                                'shrink-0 text-2xs tabular-nums',
                                item.status === 'error' ? 'text-error-600' : 'text-text-tertiary',
                            )}
                        >
                            {status}
                        </span>
                        {item.status === 'error' && (
                            <RefreshCw className="size-3.5 shrink-0 text-error-500" />
                        )}
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            className="shrink-0 text-text-tertiary"
                            aria-label={t('bugReports.form.removeFile', 'Remove file')}
                            onClick={() => onRemove(item.localId)}
                        >
                            <X className="size-3.5" />
                        </Button>

                        {item.status === 'uploading' && (
                            <span
                                className="absolute inset-x-0 bottom-0 h-0.5 bg-primary-500 transition-[width]"
                                style={{ width: `${item.progress}%` }}
                            />
                        )}
                    </li>
                );
            })}
        </ul>
    );
}
